import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Práce v uzavřených prostorech - V1 DRAFT. Authored for structural
 * completeness; NOT a substitute for sign-off by a Czech osoba odborně
 * způsobilá v prevenci rizik. Legal refs per the Czech BOZP research
 * (docs/specs/2026-06-23-bozp-research-czech-legal.md):
 * vyhl. 180/2015 Sb. §5(2)(d) (zákaz práce mladistvých uvnitř uzavřených
 * nádob/nádrží) a NV 101/2005 Sb. (požadavky na pracoviště). Číselné limity
 * atmosféry (O2, LEL, CO) vycházejí z běžné průmyslové praxe a MUSÍ být
 * ověřeny odborně způsobilou osobou. Czech-only (LocalizedText cs-CZ). 3
 * chapters across the text/interactive block catalog + a graded quiz with one
 * question per kind. Answer keys live here (repo-privileged) and in the DB;
 * stripped from the client payload by the API.
 */
export const confinedSpaceProfile: TrainingProfile = {
  slug: 'confined-space',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Práce v uzavřených prostorech',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-atmosfera',
        title: t('Měření atmosféry', 'Atmosphere testing'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-atmosfera'],
        blocks: [
          {
            id: 'h-atmosfera',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Měření atmosféry v uzavřeném prostoru',
              'Testing the atmosphere in a confined space',
            ),
          },
          {
            id: 'p-atmosfera-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Uzavřený prostor (nádrž, šachta, jímka, kanál, kotel) může obsahovat **nedýchatelnou nebo výbušnou atmosféru**. Před vstupem i v jeho průběhu se atmosféra **měří kalibrovaným detektorem** - nikdy se nespoléhej na čich nebo zrak. Mnoho nebezpečných plynů je bez barvy a zápachu.',
              'A confined space (tank, shaft, sump, duct, boiler) may contain an **unbreathable or explosive atmosphere**. Before entry and throughout the work, the atmosphere is **tested with a calibrated detector** - never rely on smell or sight. Many dangerous gases are colourless and odourless.',
            ),
          },
          {
            id: 'anim-atmosfera',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'atmosphereTest',
            steps: [
              {
                label: t('Vstup povolen', 'Safe to enter'),
                say: t(
                  'Detektor ukazuje O₂ 19,5-23,5 % a žádné výbušné ani toxické plyny.',
                  'The detector shows O₂ 19.5-23.5 % and no explosive or toxic gases.',
                ),
              },
              {
                label: t('Nevstupuj', 'Do not enter'),
                say: t(
                  'Mimo limity: nevstupuj, větrej a měř znovu.',
                  'Out of limits: stay out, ventilate, measure again.',
                ),
              },
            ],
            caption: t(
              'Atmosféru měř před vstupem i průběžně - při nedostatku kyslíku nevstupuj.',
              'Test the atmosphere before entry and continuously - do not enter if oxygen is insufficient.',
            ),
          },
          {
            id: 'c-atmosfera-poradi',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Měří se ve správném pořadí: **1. kyslík (O₂)**, **2. hořlavé plyny a páry (% DMV / LEL)**, **3. toxické plyny (CO, H₂S)**. Odebírej vzorky v několika výškách - některé plyny jsou těžší než vzduch a klesají ke dnu.',
              'Test in the correct order: **1. oxygen (O₂)**, **2. flammable gases and vapours (% LEL)**, **3. toxic gases (CO, H₂S)**. Take samples at several heights - some gases are heavier than air and sink to the bottom.',
            ),
          },
          {
            id: 'cl-atmosfera',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Kalibrovaný a funkční vícečidlový detektor (O₂, LEL, CO, H₂S)',
                'A calibrated, working multi-sensor detector (O₂, LEL, CO, H₂S)',
              ),
              t(
                'Bump test / kontrola funkce před každým použitím',
                'Bump test / function check before each use',
              ),
              t(
                'Obsah kyslíku v bezpečném rozmezí 19,5-23,5 % obj.',
                'Oxygen content within the safe range of 19.5-23.5 % vol.',
              ),
              t(
                'Koncentrace hořlavin pod 10 % dolní meze výbušnosti (DMV)',
                'Flammable concentration below 10 % of the lower explosive limit (LEL)',
              ),
              t(
                'Trvalé (kontinuální) monitorování po celou dobu práce',
                'Continuous monitoring for the whole duration of the work',
              ),
            ],
          },
          {
            id: 'c-atmosfera-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Při **poklesu kyslíku pod 19,5 %** nebo **nárůstu hořlavin nad povolenou mez** spustí detektor alarm - okamžitě **opusť prostor**. Doplňování kyslíku z lahve do atmosféry je zakázáno (riziko prudkého hoření).',
              'If **oxygen drops below 19.5 %** or **flammables rise above the permitted limit**, the detector triggers an alarm - **leave the space** immediately. Topping up the atmosphere with oxygen from a cylinder is forbidden (risk of violent combustion).',
            ),
          },
          {
            id: 'kc-atmosfera',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Která veličina se v uzavřeném prostoru měří jako první?',
              'Which quantity is measured first in a confined space?',
            ),
            choices: [
              { id: 'kc-a', label: t('Obsah kyslíku (O₂)', 'Oxygen content (O₂)') },
              { id: 'kc-b', label: t('Teplota', 'Temperature') },
              { id: 'kc-c', label: t('Vlhkost', 'Humidity') },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Nejprve se ověřuje kyslík, protože jeho nedostatek i přebytek je bezprostředně životu nebezpečný a ovlivňuje měření hořlavin.',
              'Oxygen is checked first because both a deficiency and an excess are immediately life-threatening and affect the flammables reading.',
            ),
          },
        ],
      },
      {
        id: 'ch-povoleni',
        title: t('Povolení (příkaz V)', 'Entry permit (permit "V")'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-povoleni'],
        blocks: [
          {
            id: 'h-povoleni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Povolení ke vstupu - příkaz „V"', 'Entry permit - permit "V"'),
          },
          {
            id: 'p-povoleni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Vstup do uzavřeného prostoru je možný až po vydání **písemného povolení (příkazu V)** odpovědnou osobou. Povolení stanoví **podmínky bezpečné práce**: výsledky měření, způsob větrání, OOPP, zajištění proti spuštění technologie a obsazení funkce **dozoru u vstupu**.',
              'Entry into a confined space is possible only after a **written permit (permit V)** has been issued by a responsible person. The permit sets out the **conditions for safe work**: test results, the ventilation method, PPE, safeguards against the plant starting up, and the appointment of an **attendant at the entry**.',
            ),
          },
          {
            id: 'c-povoleni-zakon',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní rámec: **NV 101/2005 Sb.** o podrobnějších požadavcích na pracoviště a pracovní prostředí. Práci mladistvých uvnitř uzavřených nádob a nádrží zakazuje **vyhl. 180/2015 Sb. §5 odst. 2 písm. d)** - vstup je tedy možný až od **18 let**.',
              'Legal framework: **Gov. Reg. 101/2005 Coll.** on more detailed requirements for the workplace and working environment. Work by minors inside closed vessels and tanks is prohibited by **Decree 180/2015 Coll. §5(2)(d)** - so entry is possible only from **18 years of age**.',
            ),
          },
          {
            id: 'ord-povoleni',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky před vstupem do uzavřeného prostoru do správného pořadí.',
              'Order the steps before entering a confined space.',
            ),
            items: [
              {
                id: 'op-1',
                label: t(
                  'Odpojení a zajištění technologie (LOTO), uzavření přívodů',
                  'Isolate and lock out the plant (LOTO), close off the feeds',
                ),
              },
              {
                id: 'op-2',
                label: t('Větrání a měření atmosféry', 'Ventilate and test the atmosphere'),
              },
              {
                id: 'op-3',
                label: t(
                  'Vydání písemného povolení (příkazu V)',
                  'Issue the written permit (permit V)',
                ),
              },
              {
                id: 'op-4',
                label: t(
                  'Obsazení dozoru u vstupu a vstup pracovníka',
                  'Post the attendant at the entry and let the worker enter',
                ),
              },
            ],
          },
          {
            id: 'c-povoleni-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Bez platného povolení a bez dozoru u vstupu se do uzavřeného prostoru nikdy nevstupuje.** Dozor zůstává po celou dobu vně prostoru a udržuje s pracovníkem stálé spojení.',
              '**Never enter a confined space without a valid permit and without an attendant at the entry.** The attendant stays outside the space the whole time and keeps constant contact with the worker.',
            ),
          },
          {
            id: 'k-povoleni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Vstup povoluje **písemný příkaz V**, vždy s **dozorem u vstupu**. Mladiství (do 18 let) mají vstup zakázán.',
              'Entry is authorised by a **written permit V**, always with an **attendant at the entry**. Minors (under 18) are prohibited from entering.',
            ),
          },
        ],
      },
      {
        id: 'ch-zachrana',
        title: t('Plán záchrany', 'Rescue plan'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-zachrana',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Plán záchrany a zákaz vstupu bez zajištění',
              'Rescue plan and no entry without safeguards',
            ),
          },
          {
            id: 'p-zachrana-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Před zahájením práce musí být zpracován **plán záchrany**. Většina obětí v uzavřených prostorech jsou **zachránci bez vybavení**, kteří vběhnou dovnitř za nehybným kolegou. Záchrana se proto vede **zvenčí** - pomocí postroje, lana a vyprošťovacího zařízení (trojnožky).',
              'Before work begins, a **rescue plan** must be prepared. Most fatalities in confined spaces are **unequipped rescuers** who rush in after a motionless colleague. Rescue is therefore carried out **from the outside** - using a harness, a rope and a retrieval device (a tripod).',
            ),
          },
          {
            id: 'acc-zachrana',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Proč se nesmí vběhnout dovnitř bez vybavení?',
                  'Why must you not rush in without equipment?',
                ),
                a: t(
                  'Nedýchatelná atmosféra způsobí ztrátu vědomí během několika vteřin - z neopatrného zachránce se stane druhá oběť. Vstup je možný jen s vlastním přístrojem na ochranu dýchacích cest a jištěním.',
                  'An unbreathable atmosphere causes loss of consciousness within seconds - a careless rescuer becomes the second victim. Entry is possible only with a self-contained breathing apparatus and a safety line.',
                ),
              },
              {
                q: t('Jak probíhá záchrana zvenčí?', 'How is rescue from the outside carried out?'),
                a: t(
                  'Pracovník je jištěn lanem vedeným přes vyprošťovací zařízení; dozor jej v případě nehody vytáhne bez nutnosti vstupu, přivolá ZZS na lince 155 a sleduje životní funkce.',
                  'The worker is secured by a rope run over a retrieval device; in an emergency the attendant hauls them out without having to enter, calls the emergency medical service on 155 and monitors vital signs.',
                ),
              },
            ],
          },
          {
            id: 'c-zachrana-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Záchranné vybavení (postroj, lano, trojnožka, autonomní dýchací přístroj) musí být **připraveno u vstupu před zahájením práce**, ne až ve chvíli nehody.',
              'Rescue equipment (harness, rope, tripod, self-contained breathing apparatus) must be **ready at the entry before work starts**, not only at the moment of an accident.',
            ),
          },
          {
            id: 'k-zachrana',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Bez **plánu záchrany** se nezačíná. Záchrana se vede **zvenčí**. Tísňová linka: **155**.',
              'Do not start without a **rescue plan**. Rescue is carried out **from the outside**. Emergency line: **155**.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-kyslik',
      kind: 'numeric',
      chapterId: 'ch-atmosfera',
      prompt: t(
        'Jaký je minimální bezpečný obsah kyslíku v atmosféře (v % objemových) pro vstup do uzavřeného prostoru?',
        'What is the minimum safe oxygen content in the atmosphere (in % by volume) for entry into a confined space?',
      ),
      correct: 19.5,
      tolerance: 0,
    },
    {
      id: 'q-poradi-mereni',
      kind: 'single',
      chapterId: 'ch-atmosfera',
      prompt: t('Která veličina se měří jako první?', 'Which quantity is measured first?'),
      choices: [
        { id: 'a', label: t('Hořlavé plyny (LEL)', 'Flammable gases (LEL)') },
        { id: 'b', label: t('Kyslík (O₂)', 'Oxygen (O₂)') },
        { id: 'c', label: t('Oxid uhelnatý (CO)', 'Carbon monoxide (CO)') },
      ],
      correct: 'b',
    },
    {
      id: 'q-povoleni-tf',
      kind: 'truefalse',
      chapterId: 'ch-povoleni',
      prompt: t(
        'Do uzavřeného prostoru lze vstoupit bez písemného povolení, pokud je práce krátká.',
        'You can enter a confined space without a written permit if the work is short.',
      ),
      correct: false,
    },
    {
      id: 'q-povoleni-multi',
      kind: 'multi',
      chapterId: 'ch-povoleni',
      prompt: t(
        'Co musí být zajištěno před vstupem do uzavřeného prostoru? (vyber všechny správné)',
        'What must be in place before entering a confined space? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Písemné povolení (příkaz V)', 'A written permit (permit V)') },
        { id: 'b', label: t('Měření atmosféry', 'Atmosphere testing') },
        { id: 'c', label: t('Dozor u vstupu', 'An attendant at the entry') },
        {
          id: 'd',
          label: t('Souhlas spolupracovníka po telefonu', "A co-worker's consent over the phone"),
        },
      ],
      correct: ['a', 'b', 'c'],
    },
    {
      id: 'q-vstup-order',
      kind: 'ordering',
      chapterId: 'ch-povoleni',
      prompt: t(
        'Seřaď kroky před vstupem do uzavřeného prostoru.',
        'Order the steps before entering a confined space.',
      ),
      choices: [
        { id: 'a', label: t('Zajištění technologie (LOTO)', 'Lock out the plant (LOTO)') },
        { id: 'b', label: t('Větrání a měření atmosféry', 'Ventilate and test the atmosphere') },
        { id: 'c', label: t('Vydání povolení (příkaz V)', 'Issue the permit (permit V)') },
        { id: 'd', label: t('Obsazení dozoru a vstup', 'Post the attendant and enter') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
